/**
 * The document list's one date column: the upload date or the file's own
 * modification date, whichever the viewer picked in the column header
 * (`useDocumentListDateBasis`). The tooltip says which date it is, in full,
 * and — when the table is too narrow for the Uploader column — who uploaded it.
 */

import { useDocumentListDateBasis } from "@/domains/documents/hooks/useDocumentListDateBasis";
import {
  fileDateHint,
  formatDocumentDate,
  formatDocumentDateTime,
  resolveFileDate,
} from "@/domains/documents/utils/documentDates";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

import { DateHint } from "./DateHint";

interface DocumentDateCellProps {
  document: {
    created_at?: string | null;
    original_created_at?: string | null;
    document_content_modified_date?: string | null;
  };
  /** Pass only when the Uploader column is hidden; the tooltip then names them. */
  uploaderName?: string | null;
  className?: string;
}

export function DocumentDateCell({ document, uploaderName, className }: DocumentDateCellProps) {
  const { t } = useLanguage();
  const basis = useDocumentListDateBasis();
  const uploader = uploaderName?.trim();
  // A function replacer, so a `$&` or `$1` in someone's name is inserted as written.
  const uploadedBy = uploader
    ? t.documents.table.uploadedByTooltip.replace("{name}", () => uploader)
    : null;
  const withUploader = (hint: string) => (uploadedBy ? `${hint} ${uploadedBy}` : hint);
  const cell = cn("flex items-center justify-center text-sm text-muted-foreground", className);
  // No date to show: the dash still carries who uploaded it, when the
  // Uploader column has been dropped.
  const noDate = uploadedBy ? <DateHint hint={uploadedBy}>-</DateHint> : "-";

  if (basis === "upload") {
    return (
      <div className={cell}>
        {document.created_at ? (
          <DateHint
            hint={withUploader(
              t.documents.table.uploadedAtTooltip.replace(
                "{date}",
                formatDocumentDateTime(document.created_at)
              )
            )}
          >
            {formatDocumentDate(document.created_at)}
          </DateHint>
        ) : (
          noDate
        )}
      </div>
    );
  }

  const fileDate = resolveFileDate(document);
  return (
    <div className={cell}>
      {fileDate ? (
        <DateHint hint={withUploader(fileDateHint(fileDate, t.documents.table))}>
          {formatDocumentDate(fileDate.iso)}
        </DateHint>
      ) : (
        noDate
      )}
    </div>
  );
}
